import React, { useState } from 'react';
import {
  EnvelopeSimple, WebhooksLogo, Timer, ChatCircle, HandTap,
  Check, CaretRight,
} from '@phosphor-icons/react';

const TRIGGER_OPTIONS = [
  {
    type: 'email',
    label: 'A new enquiry is received',
    description: 'Runs when a new email arrives',
    icon: EnvelopeSimple,
  },
  {
    type: 'webhook',
    label: 'Someone submits a form',
    description: 'Runs when an event is received',
    icon: WebhooksLogo,
  },
  {
    type: 'sms',
    label: 'A message is received',
    description: 'Runs when an SMS arrives',
    icon: ChatCircle,
  },
  {
    type: 'schedule',
    label: 'On a schedule',
    description: 'Runs at set times automatically',
    icon: Timer,
  },
  {
    type: 'manual',
    label: 'Manually',
    description: 'Runs when you click Run Now',
    icon: HandTap,
  },
];

const SCHEDULE_FREQUENCIES = [
  { value: 'hourly', label: 'Every hour' },
  { value: 'daily', label: 'Every day' },
  { value: 'weekly', label: 'Every week' },
  { value: 'monthly', label: 'Every month' },
];

export function TriggerLabel({ type }) {
  const opt = TRIGGER_OPTIONS.find(t => t.type === type) || TRIGGER_OPTIONS[4];
  const Icon = opt.icon;
  return (
    <div className="flex items-center gap-2" data-testid="trigger-label">
      <Icon size={13} className="text-[#00E5C0]" />
      <span className="text-[12px] text-[#B0B8C4]">{opt.label}</span>
    </div>
  );
}

export default function TriggerSelector({
  selected,
  config = {},
  onSelect,
  onConfigChange,
  compact = false,
}) {
  const [showScheduleConfig, setShowScheduleConfig] = useState(selected === 'schedule');

  const handleSelect = (type) => {
    onSelect(type);
    setShowScheduleConfig(type === 'schedule');
    if (type === 'schedule' && !config.frequency) {
      onConfigChange({ frequency: 'daily', time: '09:00' });
    } else if (type !== 'schedule') {
      onConfigChange({});
    }
  };

  if (compact) {
    // Compact single-line selector for edit mode
    return (
      <div className="space-y-2" data-testid="trigger-selector-compact">
        {TRIGGER_OPTIONS.map(opt => {
          const Icon = opt.icon;
          const isSelected = selected === opt.type;
          return (
            <div key={opt.type}>
              <button
                onClick={() => handleSelect(opt.type)}
                data-testid={`trigger-opt-${opt.type}`}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                  isSelected
                    ? 'border-[rgba(0,229,192,0.2)] bg-[rgba(0,229,192,0.04)]'
                    : 'border-[rgba(255,255,255,0.05)] bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(255,255,255,0.04)]'
                }`}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isSelected ? 'bg-[rgba(0,229,192,0.12)]' : 'bg-[rgba(255,255,255,0.04)]'
                }`}>
                  <Icon size={14} className={isSelected ? 'text-[#00E5C0]' : 'text-[#7D8896]'} />
                </div>
                <div className="flex-1 min-w-0">
                  <span className={`text-[12px] font-medium ${isSelected ? 'text-[#F0F4F8]' : 'text-[#B0B8C4]'}`}>
                    {opt.label}
                  </span>
                </div>
                {isSelected && <Check size={14} className="text-[#00E5C0]" />}
              </button>

              {/* Schedule config inline */}
              {isSelected && opt.type === 'schedule' && (
                <div className="ml-10 mt-2 flex items-center gap-3" data-testid="schedule-config">
                  <select
                    value={config.frequency || 'daily'}
                    onChange={e => onConfigChange({ ...config, frequency: e.target.value })}
                    data-testid="schedule-frequency"
                    className="bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] rounded-lg px-3 py-1.5 text-[12px] text-[#C8D1DB] focus:outline-none focus:border-[rgba(0,229,192,0.3)]"
                  >
                    {SCHEDULE_FREQUENCIES.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                  {config.frequency !== 'hourly' && (
                    <div className="flex items-center gap-1">
                      <span className="text-[11px] text-[#5C6778]">at</span>
                      <input
                        type="time"
                        value={config.time || '09:00'}
                        onChange={e => onConfigChange({ ...config, time: e.target.value })}
                        data-testid="schedule-time"
                        className="bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] rounded-lg px-2 py-1.5 text-[12px] text-[#C8D1DB] focus:outline-none focus:border-[rgba(0,229,192,0.3)]"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Full card selector for create flow
  return (
    <div data-testid="trigger-selector">
      <p className="text-[11px] font-semibold text-[#5C6778] uppercase tracking-wider mb-3">Runs when...</p>
      <div className="space-y-2">
        {TRIGGER_OPTIONS.map(opt => {
          const Icon = opt.icon;
          const isSelected = selected === opt.type;
          return (
            <div key={opt.type}>
              <button
                onClick={() => handleSelect(opt.type)}
                data-testid={`trigger-${opt.type}`}
                className={`w-full flex items-center gap-3 p-3.5 rounded-xl border transition-all text-left ${
                  isSelected
                    ? 'border-[rgba(0,229,192,0.2)] bg-[rgba(0,229,192,0.04)]'
                    : 'border-[rgba(255,255,255,0.05)] bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(0,229,192,0.02)] hover:border-[rgba(255,255,255,0.08)]'
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isSelected ? 'bg-[rgba(0,229,192,0.12)]' : 'bg-[rgba(255,255,255,0.04)]'
                }`}>
                  <Icon size={16} className={isSelected ? 'text-[#00E5C0]' : 'text-[#7D8896]'} />
                </div>
                <div className="flex-1 min-w-0">
                  <span className={`text-[13px] font-medium block ${isSelected ? 'text-[#F0F4F8]' : 'text-[#C8D1DB]'}`}>
                    {opt.label}
                  </span>
                  <span className="text-[11px] text-[#5C6778] block">{opt.description}</span>
                </div>
                {isSelected ? (
                  <Check size={16} className="text-[#00E5C0] flex-shrink-0" />
                ) : (
                  <CaretRight size={14} className="text-[#3A4150] flex-shrink-0" />
                )}
              </button>

              {/* Schedule config */}
              {isSelected && opt.type === 'schedule' && (
                <div className="ml-11 mt-2 flex items-center gap-3" data-testid="schedule-config">
                  <select
                    value={config.frequency || 'daily'}
                    onChange={e => onConfigChange({ ...config, frequency: e.target.value })}
                    data-testid="schedule-frequency"
                    className="bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] rounded-lg px-3 py-2 text-[12px] text-[#C8D1DB] focus:outline-none focus:border-[rgba(0,229,192,0.3)]"
                  >
                    {SCHEDULE_FREQUENCIES.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                  {config.frequency !== 'hourly' && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] text-[#5C6778]">at</span>
                      <input
                        type="time"
                        value={config.time || '09:00'}
                        onChange={e => onConfigChange({ ...config, time: e.target.value })}
                        data-testid="schedule-time"
                        className="bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] rounded-lg px-3 py-2 text-[12px] text-[#C8D1DB] focus:outline-none focus:border-[rgba(0,229,192,0.3)]"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
